import { Box, Checkbox, Table, TableBody, TableCell, TableRow, TableContainer, Paper } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../css/style.css"
import "../css/swal.style.css"
import { AddAccountButton, DeleteAccountButton, InitializeAccountButton, ListHeader } from "./accountList.styles";
import AddAccountModal from "./addAccount";
import InitializeAccountModal from "./initializeAccount";
import SwalUtil from "../../shared/util/sweetalert/sweetalertUtil";
import { BackdropLoading } from '../../shared/spinner/backdropLoading';
import { sessionDisconnectedCheck } from '../../shared/api/sessionDisconnectedCheck';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { SystemErrorMsg, SystemErrorTitle, SystemError } from '../../constants';
import activateSessionApi from '../../shared/api/activateSession';
import accountListApi from '../../shared/api/getAccountList';
import accountChangeApi from '../../shared/api/accountChange';


const LIST_HEADER: any[] = [
    ['通番'],
    ['メールアドレス'],
    ['管理者'],
    [''],
    ['']
];

// 画面表示用アカウント情報
interface AccountList {
    id: number,
    mail: string,
    isAdmin: boolean
}

// APIレスポンス 契約期間
interface ContractDate {
    usingStartDate: string,
    usingEndDate: string
}

// APIレスポンス アカウント情報
interface AccountData {
    userMailAddress: string,
    accountInfo: string
}

// 画面ID
const screenId = "U030"
const addScreenId = "U031"
const initScreenId = "U032"
const delScreenId = "U033"
const AccountList: FC = () => {
    const [accountList, setAccountList] = useState<AccountList[]>()
    const [contractDate, setContractDate] = useState<ContractDate>()
    const [openAddAccount, setOpenAddAccount] = useState(false)
    const [openInitializeAccount, setOpenInitializeAccount] = useState(false)
    const [loading, setLoading] = useState(false);
    const [initMail, setInitMail] = useState("");
    const navigate = useNavigate();
    let setConcurrentFlg = false;

    useEffect(() => {
        const interval = setInterval(async () => {
            // セッションの有効性確認
            if (await sessionDisconnectedCheck(screenId, "polling", navigate)) {
                return;
            }
        }, 30000);
        return () => clearInterval(interval);
    }, []);

    const multipleLoginCheck = async () => {
        // 多重ログイン確認用ダイアログ
        SwalUtil.labelInfo({
            title: "多重ログイン",
            message: '他のユーザーがログイン中です。<br/>操作を続けますか？',
            textAlign: "left",
            confirmButtonText: "OK",
            denyButtonText: "キャンセル",
            buttonAlign: "right",
            buttonOrder: { confirm: 2, deny: 1 }
        }).then(async (result) => {
            // OK 押下時の処理
            if (result.isConfirmed) {
                setLoading(true);
                // セッション有効化APIを呼び出す
                const activateSessionResponse = await activateSessionApi();
                setLoading(false);
                if (await activateSessionResponse === SystemError) {
                    navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
                }
                sessionStorage.setItem("isLoginChecked", "true");
            } else if (result.isDenied) {
                // キャンセル 押下時はサインアウトする
                signOut();
            }
            setConcurrentFlg = false;
        });
    }

    const gatAccountList = async () => {
        try {
            // アカウント情報取得API
            setLoading(true)
            const accountListApiResponce = await accountListApi();
            if (accountListApiResponce.resCd === SystemError) {
                navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
            }
            const accountDataResponce = accountListApiResponce.data.accountData
            const contractDateResponce = accountListApiResponce.data.contractDate[0]
            // 管理者が一番上になるようにソート
            accountDataResponce.sort((a: AccountData, b: AccountData) => {
                return Number(a.accountInfo) - Number(b.accountInfo);
            });
            // レスポンスのアカウント情報を画面表示用に変換
            const accountDataList: AccountList[] = []
            accountDataResponce.map((v: AccountData, i: number) => {
                let account = {
                    id: 0,
                    mail: "",
                    isAdmin: false
                }
                account.id = i + 1;
                account.mail = v.userMailAddress;
                account.isAdmin = v.accountInfo === "0" ? true : false;
                accountDataList.push(account);
            })
            // アカウント情報のセット
            setAccountList(accountDataList);

            // 契約期間の設定
            let startDate = contractDateResponce.usingStartDate;
            let endDate = contractDateResponce.usingEndDate;
            // 契約開始日/契約終了日を「yyyy/mm/dd」形式に変換
            let contractDate: ContractDate = {
                usingStartDate: startDate.replace(/(\d{4})(\d{2})(\d{2})/, '$1/$2/$3'),
                usingEndDate: endDate.replace(/(\d{4})(\d{2})(\d{2})/, '$1/$2/$3')
            }
            // 契約期間のセット
            setContractDate(contractDate)
            setLoading(false)
        } catch (e) {
            navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
        }
    }

    useEffect(() => {
        (async () => {
            /** 多重ログインフラグ取得 */
            const session = await fetchAuthSession();
            const concurrentFlg = session?.tokens?.idToken?.payload?.concurrentFlg;
            const isLoginChecked = sessionStorage.getItem("isLoginChecked");
            if (concurrentFlg == "1") {
                setConcurrentFlg = true;
            }
            if (setConcurrentFlg && !isLoginChecked) {
                // 多重ログイン確認用ダイアログ
                await multipleLoginCheck();
            }

            // アカウント情報一覧取得
            await gatAccountList();
        })()
    }, []);

    // アカウント追加押下
    const handleAddAccount = async () => {
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(addScreenId, "handleAddAccount", navigate)) {
            return
        }
        setOpenAddAccount(true);
    }
    // アカウント変更押下
    const handleInitializeAccount = async (mailAddress: string) => {
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(initScreenId, "handleInitializeAccount", navigate)) {
            return
        }
        setInitMail(mailAddress);
        setOpenInitializeAccount(true);
    }
    const handleDleteAccount = async (mailAddress: string) => {
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(delScreenId, "handleDleteAccount", navigate)) {
            return
        }
        // ログインユーザ取得
        const session = await fetchAuthSession();
        let login = session?.tokens?.idToken?.payload.email;
        const message = `
                以下のアカウントを削除します。<br>
                よろしいでしょうか？<br>
                メールアドレス：${mailAddress}<br>
                <span style="color: red">※削除したアカウントは復元できません。</span>
            `
        SwalUtil.labelWarning({
            title: "アカウントの削除",
            message: message,
            textAlign: "leftWar",
            confirmButtonText: "OK",
            denyButtonText: "キャンセル",
            buttonAlign: "right",
            buttonOrder: { confirm: 2, deny: 1 }
        }).then(async (result) => {
            if (result.isConfirmed) {
                /** OKボタン押下時の処理 */
                // セッションの有効性確認
                if (await sessionDisconnectedCheck(delScreenId, "handleDlete", navigate)) {
                    return
                }
                setLoading(true)
                // アカウント情報変更API 操作フラグ = 9: 削除
                const response = await accountChangeApi(mailAddress, "", "9")
                if (response === SystemError) {
                    navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
                }
                if (login === mailAddress) {
                    await fetchAuthSession({ forceRefresh: true });
                }
                setLoading(false)
                await gatAccountList();
                navigate("/account", {});
            }
        });
    }

    return (
        <>
            <BackdropLoading open={loading} />
            <Box className="headerLabel">
                アカウント変更
            </Box>
            契約期間：{contractDate?.usingStartDate}～{contractDate?.usingEndDate}
            <Box p={3}>
                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table stickyHeader={true} size="small" sx={{ "tr:nth-of-type(even)": { backgroundColor: "#F2F2F2" } }}>
                        <ListHeader>
                            <TableRow>
                                <TableCell sx={{ width: '5%' }}>
                                    {LIST_HEADER[0]}
                                </TableCell>
                                <TableCell sx={{ width: '55%' }}>
                                    {LIST_HEADER[1]}
                                </TableCell>
                                <TableCell sx={{ width: '20%' }}>
                                    {LIST_HEADER[2]}
                                </TableCell>
                                <TableCell sx={{ width: '10%' }}></TableCell>
                                <TableCell sx={{ width: '10%' }}></TableCell>
                            </TableRow>
                        </ListHeader>
                        <TableBody>
                            {accountList?.map((v) =>
                                <TableRow key={v.id}>
                                    <TableCell>
                                        {v.id}
                                    </TableCell>
                                    <TableCell>
                                        {v.mail}
                                    </TableCell>
                                    <TableCell>
                                        <Checkbox
                                            size="small"
                                            disabled={true}
                                            checked={v.isAdmin}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <InitializeAccountButton
                                            disabled={v.isAdmin}
                                            variant="contained"
                                            onClick={() => handleInitializeAccount(v.mail)}
                                        >
                                            変更
                                        </InitializeAccountButton>
                                    </TableCell>
                                    <TableCell>
                                        <DeleteAccountButton
                                            disabled={v.isAdmin}
                                            variant="contained"
                                            onClick={() => handleDleteAccount(v.mail)}>
                                            削除
                                        </DeleteAccountButton>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            <Box sx={{ position: 'relative', minHeight: '2.5em', marginBottom: '1.5em' }}  >
                <AddAccountButton variant="contained" onClick={() => handleAddAccount()}>アカウント追加</AddAccountButton>
            </Box>
            <AddAccountModal isOpen={openAddAccount} handleClose={() => setOpenAddAccount(false)} onAction={gatAccountList} />
            <InitializeAccountModal isOpen={openInitializeAccount} handleClose={() => setOpenInitializeAccount(false)} mail={initMail} onAction={gatAccountList} />
        </>
    );
}
export default AccountList;
